"use client";

import { useState, type FC, type ReactNode, type Ref } from "react";
import {
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  UserGroupIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import type { PractitionerSearchDto, TaxonomyOption } from "cmw-client";
import { Button } from "@/components/button";
import {
  filterCount,
  GENDER_OPTIONS,
  toFilterGroups,
  toFilterOptions,
  type DirectoryFilters,
  type FilterKey,
} from "@/components/directory-filter-bar";
import { AllFiltersTrigger } from "@/components/directory-filter-bar/all-filters-trigger";
import { MobileFilterBar } from "@/components/directory-filter-bar/mobile-filter-bar";
import { MultiSelect, NestedMultiCombobox } from "@/components/input";
import type { TurnstileInstance } from "@/components/turnstile";
import { cn } from "@/lib/cn";
import { LlmSearchInput } from "./llm-search-input";

type LlmFilterBarProps = {
  filters: DirectoryFilters;
  onChange: (filters: DirectoryFilters) => void;
  taxonomy?: Record<string, TaxonomyOption[]>;
  viewMode?: "list" | "map";
  onViewModeChange?: (viewMode: "list" | "map") => void;
  onSearch: (query: string) => void;
  isSearching: boolean;
  searchResult?: PractitionerSearchDto;
  isSearchError: boolean;
  turnstileRef: Ref<TurnstileInstance | undefined>;
};

const SUBGRID = "lg:row-span-2 lg:grid lg:grid-rows-subgrid lg:gap-8";

const label = (icon: ReactNode, text: string) => (
  <span className="flex items-center gap-8">
    <span className="text-brand-blue">{icon}</span>
    {text}
  </span>
);

/**
 * A copy of DirectoryFilterBar behind an accordion, with the natural-language search above it.
 * The page opens as just the search bar; the manual controls appear once a search settles —
 * carrying whatever the model understood, or empty if it understood nothing — so they are
 * always the fallback rather than a dead end.
 */
export const LlmFilterBar: FC<LlmFilterBarProps> = ({
  filters,
  onChange,
  taxonomy,
  viewMode,
  onViewModeChange,
  onSearch,
  isSearching,
  searchResult,
  isSearchError,
  turnstileRef,
}) => {
  const set = (key: FilterKey) => (values: string[]) => onChange({ ...filters, [key]: values });

  /* Open once a search has settled, however it went. Derived rather than synced, so the first
     result reveals the controls without an effect; once the user has toggled it themselves
     their choice wins and a later search will not reopen what they closed. */
  const [override, setOverride] = useState<boolean | null>(null);
  const hasSettled = searchResult !== undefined || isSearchError;
  const isOpen = override ?? hasSettled;

  const active = filterCount(filters);

  return (
    <div className="flex flex-col gap-16">
      <LlmSearchInput
        onSearch={onSearch}
        isSearching={isSearching}
        result={searchResult}
        isError={isSearchError}
        turnstileRef={turnstileRef}
      />

      <div className="flex flex-col gap-16">
        <button
          type="button"
          onClick={() => setOverride(!isOpen)}
          aria-expanded={isOpen}
          aria-controls="llm-manual-filters"
          className="flex items-center gap-8 self-start text-15 font-semibold text-neutral-black transition-colors hover:text-brand-dark-blue"
        >
          <AdjustmentsHorizontalIcon className="size-18 text-brand-blue" />
          {isOpen ? "Hide filters" : "Refine with filters"}
          {active > 0 && (
            <span className="rounded-circle bg-brand-blue px-8 py-2 text-12 font-bold text-neutral-white">
              {active}
            </span>
          )}
          <ChevronDownIcon className={cn("size-16 transition-transform", isOpen && "rotate-180")} />
        </button>

        <div id="llm-manual-filters" hidden={!isOpen} className="flex flex-col gap-16">
          <MobileFilterBar
            className="md:hidden"
            filters={filters}
            onChange={onChange}
            taxonomy={taxonomy}
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
          />

          <div className="grid gap-16 max-md:hidden lg:grid-cols-[repeat(3,minmax(0,1fr))_auto] lg:grid-rows-[auto_auto] lg:items-start">
            <MultiSelect
              containerClassName={SUBGRID}
              label={label(<UsersIcon className="size-18" />, "Gender preference")}
              placeholder="Please select"
              maxVisibleTags={2}
              options={GENDER_OPTIONS}
              value={filters.gender ?? []}
              onValueChange={set("gender")}
            />
            <NestedMultiCombobox
              containerClassName={SUBGRID}
              label={label(<DocumentTextIcon className="size-18" />, "Areas of support")}
              placeholder="Showing all"
              maxVisibleTags={2}
              groups={toFilterGroups(taxonomy?.focusAreas)}
              value={filters.focusAreas ?? []}
              onValueChange={set("focusAreas")}
            />
            <MultiSelect
              containerClassName={SUBGRID}
              label={label(<UserGroupIcon className="size-18" />, "Client type")}
              placeholder="Please select"
              maxVisibleTags={2}
              options={toFilterOptions(taxonomy?.clientTypes)}
              value={filters.clientTypes ?? []}
              onValueChange={set("clientTypes")}
            />
            <AllFiltersTrigger filters={filters} onChange={onChange} taxonomy={taxonomy}>
              <Button
                variant="secondary-white"
                size="l"
                icon={<AdjustmentsHorizontalIcon className="size-20" />}
                className="lg:col-start-4 lg:row-start-2 lg:shrink-0"
              >
                All Filters
              </Button>
            </AllFiltersTrigger>
          </div>
        </div>
      </div>
    </div>
  );
};
